import { Paper } from '@mantine/core';
import { featureCollection } from '@turf/turf';
import { MapEvent, MapMouseEvent } from 'mapbox-gl';
import { FC, useRef, useEffect } from 'react';
import { MapRef } from 'react-map-gl';

import { FieldAnswer } from '../../../lib/types/validation';

import {
  PfMap,
  PlotsLayer,
  StyleControl,
  zoomOnFeature,
  PLOTS_LAYER_ID,
} from '@map';

const INTERACTIVE_LAYER_IDS = [PLOTS_LAYER_ID, `${PLOTS_LAYER_ID}_symbol`];

interface ValidationFormMapProps {
  fieldAnswers: FieldAnswer[];
  selectedFieldId?: number | null;
  onFieldClick?: (fieldId: number) => void;
}

export const ValidationFormMap: FC<ValidationFormMapProps> = ({
  fieldAnswers,
  selectedFieldId,
  onFieldClick,
}) => {
  const mapRef = useRef<MapRef>(null);

  const fieldsCollection = featureCollection(
    fieldAnswers.map((fa) => fa.field),
  );

  const handleLoad = (map: MapEvent) => {
    if (fieldsCollection.features.length > 0) {
      zoomOnFeature({
        map: map.target,
        geojson: fieldsCollection,
      });
    }
  };

  const handleMapClick = (event: MapMouseEvent) => {
    if (!onFieldClick) return;

    const features = event.target.queryRenderedFeatures(event.point, {
      layers: INTERACTIVE_LAYER_IDS,
    });

    if (features.length > 0) {
      const feature = features[0];
      const fieldId = feature.properties?.id;
      if (fieldId) {
        const fieldAnswer = fieldAnswers.find(
          (fa) => fa.field.id === Number(fieldId),
        );
        if (fieldAnswer) {
          onFieldClick(fieldAnswer.id);
        }
      }
    }
  };

  useEffect(() => {
    if (selectedFieldId && mapRef.current) {
      const fieldAnswer = fieldAnswers.find(
        (fa) => fa.id === selectedFieldId,
      );
      if (fieldAnswer) {
        zoomOnFeature({
          map: mapRef.current.getMap(),
          geojson: fieldAnswer.field,
        });
      }
    }
  }, [selectedFieldId, fieldAnswers]);

  return (
    <Paper h="100%" p={0} pos="relative" w="100%">
      <PfMap
        interactiveLayerIds={INTERACTIVE_LAYER_IDS}
        onClick={handleMapClick}
        onLoad={handleLoad}
        ref={mapRef}
      >
        <StyleControl />
        <PlotsLayer data={fieldsCollection} />
      </PfMap>
    </Paper>
  );
};

export default ValidationFormMap;
